import { useSyncExternalStore } from 'react';
import { parseDate } from '@/common/utils/date';
import {
  TCoverflexAIConversation,
  TCoverflexAiConversationStore,
} from './coverflexai.types';

export const DB_NAME = 'coverflex_store';
export const CONVERSTATIONS_STORE = 'ai_conversations';

let listeners: Function[] = [];
let state: TCoverflexAiConversationStore = {
  conversations: [],
};

export function initStorage(companyPersonId: string) {
  const openRequest = indexedDB.open(DB_NAME, 1);

  // Create the store if it doesn't exist
  openRequest.onupgradeneeded = function () {
    const db = openRequest.result;
    if (!db.objectStoreNames.contains(CONVERSTATIONS_STORE)) {
      const conversationsStore = db.createObjectStore(CONVERSTATIONS_STORE, {
        keyPath: 'id',
      });
      conversationsStore.createIndex('company_person_idx', 'companyPersonId');
    }
  };

  // Load the conversations and notify the subscribers
  openRequest.onsuccess = function () {
    const db = openRequest.result;
    try {
      const transaction = db.transaction(CONVERSTATIONS_STORE, 'readonly');
      const store = transaction.objectStore(CONVERSTATIONS_STORE);
      const storeIdx = store.index('company_person_idx');
      const request = storeIdx.getAll(companyPersonId);
      request.onsuccess = function () {
        const savedConversations = request.result;
        if (Array.isArray(savedConversations)) {
          savedConversations.sort(
            (a, b) =>
              parseDate(b.savedAt).getTime() - parseDate(a.savedAt).getTime()
          );
        }
        state = {
          ...state,
          conversations: savedConversations,
        };
        notifySubscribers();
      };
    } catch {}
  };
}

export function saveConversation(newConversation: TCoverflexAIConversation) {
  const openRequest = indexedDB.open(DB_NAME, 1);
  openRequest.onsuccess = function () {
    const db = openRequest.result;
    try {
      const transaction = db.transaction(CONVERSTATIONS_STORE, 'readwrite');
      const store = transaction.objectStore(CONVERSTATIONS_STORE);
      const request = store.put(newConversation);
      request.onsuccess = function () {
        if (request.result) {
          const currentConversationIdx = state.conversations.findIndex(
            (conversation) => conversation.id === newConversation.id
          );

          if (currentConversationIdx !== -1) {
            state.conversations[currentConversationIdx] = newConversation;
          } else {
            state = {
              ...state,
              conversations: [newConversation, ...state.conversations],
            };
          }
          notifySubscribers();
        }
      };
    } catch {}
  };
}

export function deleteConversations() {
  const openRequest = indexedDB.open(DB_NAME, 1);
  openRequest.onsuccess = function () {
    const db = openRequest.result;
    try {
      const transaction = db.transaction(CONVERSTATIONS_STORE, 'readwrite');
      const store = transaction.objectStore(CONVERSTATIONS_STORE);
      store.clear();
    } catch {}
  };
}

export const CoverflexAIStorage = {
  initStorage,
  saveConversation,
  deleteConversations,
};

export function useConversationHistory() {
  const conversationStore = useSyncExternalStore(
    subscribeToStore,
    getStoreSnapshot
  );
  return conversationStore;
}

function subscribeToStore(listener: Function) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getStoreSnapshot() {
  return state;
}

function notifySubscribers() {
  listeners.forEach((listener) => listener());
}
